"use client"

import { useTranslations } from "next-intl"

import { SignupLink } from "@/components/flow/SignupEntry"
import { Badge, Button, IconButton, Row } from "@/components/ui"
import { UserIcon } from "@/components/ui/icons"
import { LocalizedClientLink } from "@/components/ui/localized-client-link"
import { ROUTES } from "@/lib/routes"
import { useSession } from "@/platform/session/use-session"
import { useGetBootstrap } from "@/platform/shopper/use-get-bootstrap"

interface AccountEntryProps {
  location: string
  className: string
  placeholderClassName: string
  iconClassName: string
}

export const AccountEntry = ({ location, className, placeholderClassName, iconClassName }: AccountEntryProps) => {
  const t = useTranslations("nav")
  const { status } = useSession()
  const signedIn = status.kind === "authenticated"
  const { data: bootstrap } = useGetBootstrap(signedIn, false)

  if (status.kind === "loading") return <span aria-hidden className={`inline-block shrink-0 ${placeholderClassName}`} />

  if (!signedIn) {
    return (
      <Button asChild variant="primary" size="sm" className={className}>
        <SignupLink location={location}>{t("signUp")}</SignupLink>
      </Button>
    )
  }

  const available = bootstrap?.creditBalance?.marketplace?.available
  const credit = available?.greaterThanZero ? available.displayValue : null

  return (
    <Row gap="200" className="shrink-0">
      {credit && (
        <Badge tone="brand" size="sm" data-testid="account-entry-credit">
          {credit}
        </Badge>
      )}
      <IconButton
        variant="ghost"
        aria-label={credit ? t("accountWithCredit", { amount: credit }) : t("account")}
        className={`text-fg-brand hover:text-brand-hover ring-line-faint ring-1 ring-inset ${iconClassName}`}
        asChild
      >
        <LocalizedClientLink href={`/${ROUTES.ACCOUNT}`}>
          <UserIcon aria-hidden size={20} strokeWidth={1.5} />
        </LocalizedClientLink>
      </IconButton>
    </Row>
  )
}
